import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import ServicesList from "../components/BookingComponents/ServicesList";
import BookingFlow from "../components/BookingComponents/BookingFlow";
import Maps from "../components/BookingComponents/Maps";
import DisplayReview from "../components/DisplayReview";
import ReviewForm from "../components/ReviewForm";
import { use } from "react";
import InstagramIcon from "../assets/instagram-white-icon.png";
import TikTokIcon from "../assets/tiktok-white-icon.png";
import XIcon from "../assets/x-social-media-white-icon.png";
import AIAssistantForm from "../components/AIAssistantForm";
import moment from "moment";
import { FaStar } from "react-icons/fa";
import { MdVerified } from "react-icons/md";
import HyveLogo from "../assets/HYVE_LogoMaster_2_1.png";
import {
  BACKGROUND_COLOR_MAP,
  TEXT_COLOR_MAP,
  FONT_SIZE_MAP,
  FONT_WEIGHT_MAP,
  BORDER_RADIUS_MAP,
  BOX_SHADOW_MAP,
} from "../design-tokens";

// ============================================================
// BOOKING PAGE CUSTOMIZATION HEADER COMPONENT
// ============================================================

const CustomizationHeader = ({ customization }) => {
  if (!customization || !customization.layers || customization.layers.length === 0) {
    return null;
  }

  const { backgroundColor, layers } = customization;
  const bgColorHex = BACKGROUND_COLOR_MAP[backgroundColor] || BACKGROUND_COLOR_MAP.white;

  // Sort layers by zIndex
  const sortedLayers = [...layers].sort((a, b) => a.zIndex - b.zIndex);

  return (
    <div
      className="booking-page-header w-full"
      style={{ backgroundColor: bgColorHex }}
    >
      {sortedLayers.map((layer) => {
        if (layer.type === "TEXT") {
          const { text, height } = layer;
          return (
            <div
              key={layer.id}
              className="layer layer-text"
              style={{
                height: height ? `${height}px` : "auto",
                minHeight: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: text.align === "left" ? "flex-start" : text.align === "right" ? "flex-end" : "center",
                padding: "0 16px",
              }}
            >
              <p
                style={{
                  fontSize: `${FONT_SIZE_MAP[text.fontSize] || 14}px`,
                  fontWeight: FONT_WEIGHT_MAP[text.weight] || 400,
                  fontStyle: text.italic ? "italic" : "normal",
                  textAlign: text.align || "center",
                  color: TEXT_COLOR_MAP[text.color] || TEXT_COLOR_MAP.black,
                  width: "100%",
                  margin: 0,
                }}
              >
                {text.value}
              </p>
            </div>
          );
        }

        if (layer.type === "IMAGE") {
          const { image, height } = layer;
          return (
            <div
              key={layer.id}
              className="layer layer-image"
              style={{
                height: height ? `${height}px` : "auto",
                width: "100%",
                padding: "8px 16px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {image.url ? (
                <img
                  src={image.url}
                  alt="Provider image"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: image.fit || "cover",
                    borderRadius: `${BORDER_RADIUS_MAP[image.rounded] || 0}px`,
                    boxShadow: BOX_SHADOW_MAP[image.shadow] || "none",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#E5E7EB",
                    borderRadius: `${BORDER_RADIUS_MAP[image.rounded] || 0}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#9CA3AF",
                  }}
                >
                  No image
                </div>
              )}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};

const BookingPage = ({ bookingSlug: subdomainSlug }) => {
  const navigate = useNavigate();
  const { userData, token } = useAuth();
  // Support both route patterns:
  // - Main app: /booking/:providerUsername (uses providerUsername param from route)
  // - Subdomain: bookingSlug passed as prop (e.g., nailzbydream.uhyve.com -> bookingSlug="nailzbydream")
  const params = useParams();
  const providerUsername = params.providerUsername || subdomainSlug;

  const [loading, setLoading] = useState(true);
  const [providerData, setProviderData] = useState(null);
  const [availability, setAvailability] = useState([]); // Separate state for availability
  const [blockedTimes, setBlockedTimes] = useState([]); // Provider's blocked time slots
  const [bookingPageCustomization, setBookingPageCustomization] = useState(null); // Provider's booking page customization
  // Which service detail is being booked
  const [selectedServiceDetail, setSelectedServiceDetail] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [showBookingFlow, setShowBookingFlow] = useState(false);
  const [rating, setRating] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(null); // Track which service is being booked

  // Error states
  const [error, setError] = useState(null);
  const [bookingError, setBookingError] = useState(null);

  const ICONS = {
    Instagram: InstagramIcon,
    X: XIcon,
    TikTok: TikTokIcon,
  };

  // All times are now in UTC
  const commonTimezones = [
    { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  ];

  useEffect(() => {
    const fetchProviderData = async () => {
      try {
        setError(null); // Clear any previous errors

        if (!providerUsername) {
          setError("No provider specified.");
          setLoading(false);
          return;
        }

        let resolvedUsername = providerUsername;

        // If we're using a booking slug (subdomain), resolve it to the actual provider username
        if (subdomainSlug) {
          console.log("Resolving booking slug:", subdomainSlug);
          try {
            const slugResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/booking-slug/resolve/${subdomainSlug}`
            );
            
            if (slugResponse.data.found && (slugResponse.data.businessName || slugResponse.data.providerUsername)) {
              // Use businessName first (this is what the provider endpoint expects), fallback to providerUsername
              resolvedUsername = slugResponse.data.businessName || slugResponse.data.providerUsername;
              console.log("Resolved booking slug to provider:", resolvedUsername);
            } else {
              setError("This booking link does not exist. Please check the URL.");
              setLoading(false);
              return;
            }
          } catch (slugErr) {
            console.error("Failed to resolve booking slug:", slugErr);
            if (slugErr.response?.status === 404) {
              setError("This booking link does not exist. Please check the URL.");
            } else {
              setError("Failed to load booking page. Please try again.");
            }
            setLoading(false);
            return;
          }
        }

        console.log("Fetching provider data for username:", resolvedUsername);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/providers/${resolvedUsername}`
        );

        setProviderData(response.data);

        // Removed booking link routing functionality
      } catch (err) {
        console.error("Failed to fetch provider data:", err.message);

        // Set user-friendly error message based on error type
        if (err.response?.status === 404) {
          setError("Provider not found. Please check the URL and try again.");
        } else if (err.response?.status === 500) {
          setError("Server error. Please try again later.");
        } else if (err.code === "NETWORK_ERROR" || !err.response) {
          setError(
            "Network error. Please check your internet connection and try again."
          );
        } else {
          setError("Failed to load provider information. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProviderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.providerUsername, subdomainSlug, navigate]);

  // Update rating from providerData when it changes
  useEffect(() => {
    if (
      providerData?.provider?.rating !== null &&
      providerData?.provider?.rating !== undefined
    ) {
      setRating(parseFloat(providerData.provider.rating) || 0);
    } else {
      setRating(0);
    }
  }, [providerData]);

  // Fetch availability separately using the new route
  useEffect(() => {
    if (!providerData?.provider?.id) return; // guard until id is available

    const fetchAvailability = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/availability/provider/${providerData.provider.id}`
        );
        setAvailability(response.data.data || []);
      } catch (err) {
        console.error("Failed to fetch availability:", err);
        // Availability fetch failure is not critical, so we don't set error state
        // Fallback to availability from providerData if available
        if (providerData.availability) {
          setAvailability(providerData.availability);
        }
      }
    };

    fetchAvailability();
  }, [providerData]);

  // Fetch provider's blocked times
  useEffect(() => {
    if (!providerData?.provider?.id) return; // guard until id is available

    const fetchBlockedTimes = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/blocked-time/provider/${providerData.provider.id}`
        );
        setBlockedTimes(response.data.data || []);
      } catch (err) {
        console.error("Failed to fetch blocked times:", err);
        // Blocked times fetch failure is not critical, proceed without them
        setBlockedTimes([]);
      }
    };

    fetchBlockedTimes();
  }, [providerData]);

  // Fetch provider's booking page customization
  useEffect(() => {
    if (!providerData?.provider?.id) return; // guard until id is available

    const fetchBookingPageCustomization = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/providers/${providerData.provider.id}/booking-page-customization`
        );
        // Only set if there's a published customization with layers
        const published = response.data.publishedCustomization;
        if (published && published.layers && published.layers.length > 0) {
          setBookingPageCustomization(published);
        } else {
          setBookingPageCustomization(null);
        }
      } catch (err) {
        console.error("Failed to fetch booking page customization:", err);
        // Customization fetch failure is not critical, proceed without it
        setBookingPageCustomization(null);
      }
    };

    fetchBookingPageCustomization();
  }, [providerData]);

  // Handler for when a service's Book button is clicked.
  const handleBookClick = async (serviceDetail, preSelectedAddons = []) => {
    setBookingLoading(serviceDetail.id); // Set loading for this specific service
    setBookingError(null); // Clear any previous booking errors

    // Add a small delay to show the loading state
    await new Promise((resolve) => setTimeout(resolve, 500));

    setSelectedServiceDetail(serviceDetail);
    setSelectedAddons(preSelectedAddons || []); // Use pre-selected addons if provided
    setShowBookingFlow(true);
    setBookingLoading(null); // Clear loading after state is set

    // Scroll to the top of the page when booking flow opens
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  };

  // Handler for booking errors from BookingFlow
  const handleBookingError = (errorMessage) => {
    setBookingError(errorMessage);

    // Scroll to the top of the page when an error occurs
    if (errorMessage) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100); // Small delay to ensure the error message is rendered
    }
  };

  // Clear booking error
  const clearBookingError = () => {
    setBookingError(null);
  };

  // Function to refresh provider data (including reviews and rating)
  const refreshProviderData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/providers/${providerUsername}`
      );
      setProviderData(response.data);
      // Rating will be updated automatically via the useEffect that watches providerData
    } catch (err) {
      console.error("Failed to refresh provider data:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading provider information...</p>
        </div>
      </div>
    );
  }

  // Error state - show error message with retry option
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-red-600 mb-2">
              Error Loading Provider
            </h2>
            <p className="text-gray-700 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!providerData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-lg">No provider data found.</div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Destructure parts of the response payload for convenience.
  // Note: availability is now fetched separately, but we keep bookings from providerData
  const { provider, policies, services, bookings, reviews } = providerData;

  // Check if the current user has already submitted a review
  const userHasReviewed =
    userData && reviews?.some((review) => review.UserId === userData.userid);

  // Determine currency based on provider's country
  const currencyInfo = (() => {
    const code = provider?.countryCodeISO || "";
    const country = provider?.country?.toLowerCase() || "";
    
    if (code === "GB" || country === "united kingdom" || country === "uk") return { symbol: "£", code: "GBP" };
    if (code === "EUR" || ["france", "germany", "italy", "spain", "ireland", "netherlands"].includes(country)) return { symbol: "€", code: "EUR" };
    if (code === "CA" || country === "canada") return { symbol: "$", code: "CAD" };
    
    // Default to USD as base currency for US and others
    return { symbol: "$", code: "USD" };
  })();

  // Determine background color from customization
  const pageBackgroundColor = bookingPageCustomization?.backgroundColor
    ? BACKGROUND_COLOR_MAP[bookingPageCustomization.backgroundColor] || "#FFFFFF"
    : "#FFFFFF";

  return (
    <div
      className="min-h-screen text-gray-900"
      style={{ backgroundColor: pageBackgroundColor }}
    >
      {/* Booking Page Customization Header */}
      {bookingPageCustomization && (
        <CustomizationHeader customization={bookingPageCustomization} />
      )}

      {/* Booking Error Display - At the top of the page */}
      {bookingError && (
        <div className="bg-red-50 border-b border-red-200 p-4">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="text-red-600 text-xl mr-2">⚠️</div>
                <div>
                  <h3 className="text-red-600 font-semibold">Booking Error</h3>
                  <p className="text-gray-700 text-sm">{bookingError}</p>
                </div>
              </div>
              <button
                onClick={clearBookingError}
                className="text-red-600 hover:text-red-700 text-xl"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Layout: two columns on desktop, one on mobile/tablet */}
        <div className="lg:flex lg:gap-8 xl:gap-12">
          {/* Left Column: Main content */}
          <div className="lg:w-7/12 xl:w-2/3 space-y-6">
            {/* Provider Info */}
            <section className="bg-white border border-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              {/* Mobile/Tablet: Stacked layout */}
              <div className="lg:hidden flex flex-col items-center space-y-4">
                {/* Profile Image */}
                <div className="flex-shrink-0 w-24 h-24">
                  <img
                    src={
                      provider.profilePicUrl && provider.profilePicUrl !== ""
                        ? provider.profilePicUrl
                        : HyveLogo
                    }
                    alt="Profile Pic"
                    className="w-full h-full object-cover rounded-full shadow-lg shadow-black/30"
                  />
                </div>

                {/* Provider Info */}
                <div className="flex flex-col items-center text-center space-y-2">
                  <h2 className="text-xl font-bold text-gray-900">
                    {provider.businessName || provider.providerUsername}
                  </h2>
                  {provider.businessName &&
                    provider.providerUsername &&
                    provider.businessName !== provider.providerUsername && (
                      <p className="text-sm text-gray-500">
                        @{provider.providerUsername}
                      </p>
                    )}
                  <p className="text-sm flex items-center gap-1 text-gray-600">
                    {rating > 0 ? (
                      <>
                        <span className="font-semibold text-gray-900">
                          {rating.toFixed(1)}
                        </span>
                        <FaStar className="text-yellow-500 text-xs" />
                        <span className="text-gray-500">
                          ({reviews?.length || 0} reviews)
                        </span>
                      </>
                    ) : (
                      "No rating yet"
                    )}
                  </p>
                  <div className="text-sm space-y-1 text-gray-600">
                    {/* Address and postal code commented out - only showing city and country */}
                    {/* {(provider.city || provider.postal) && (
                      <p>
                        {provider.city && <span>{provider.city}</span>}
                        {provider.city && provider.postal && <span>, </span>}
                        {provider.postal && <span>{provider.postal}</span>}
                      </p>
                    )} */}
                    {provider.city && <p>{provider.city}</p>}
                    {provider.country && <p>{provider.country}</p>}
                    {provider.phoneNumber && (
                      <p className="text-gray-700 font-medium">
                        {provider.phoneNumber}
                      </p>
                    )}
                  </div>

                  {provider.socialMediaHandles?.length > 0 && (
                    <div className="flex items-center space-x-3 mt-2">
                      {provider.socialMediaHandles.map(({ platform, url }) => {
                        // normalize platform names if needed
                        const icon = ICONS[platform];
                        if (!icon || !url) return null; // skip unsupported platforms or empty URLs

                        // Format the URL properly based on platform
                        let formattedUrl = url;

                        // Helper function to clean username (remove @ and any existing domain)
                        const cleanUsername = (username) => {
                          return username
                            .replace(/^@/, "") // Remove leading @
                            .replace(/^https?:\/\/(www\.)?/, "") // Remove http/https and www
                            .replace(
                              /^(instagram\.com\/|tiktok\.com\/@?|x\.com\/|twitter\.com\/)/,
                              ""
                            ) // Remove platform domains
                            .split("/")[0]; // Take only the first part if there are slashes
                        };

                        if (platform === "Instagram") {
                          if (!url.startsWith("http")) {
                            const username = cleanUsername(url);
                            formattedUrl = `https://instagram.com/${username}`;
                          }
                        } else if (platform === "TikTok") {
                          if (!url.startsWith("http")) {
                            const username = cleanUsername(url);
                            formattedUrl = `https://tiktok.com/@${username}`;
                          }
                        } else if (platform === "X") {
                          if (!url.startsWith("http")) {
                            const username = cleanUsername(url);
                            formattedUrl = `https://x.com/${username}`;
                          }
                        }

                        return (
                          <a
                            key={platform}
                            href={formattedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                          >
                            <img
                              src={icon}
                              alt={platform}
                              className="h-5 w-5 mr-1"
                            />
                            <span className="text-xs">{platform}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop: Horizontal layout */}
              <div className="hidden lg:flex items-center gap-6">
                {/* Left column (Image) */}
                <div className="flex-shrink-0 w-32 h-32">
                  <img
                    src={
                      provider.profilePicUrl && provider.profilePicUrl !== ""
                        ? provider.profilePicUrl
                        : HyveLogo
                    }
                    alt="Profile Pic"
                    className="w-full h-full object-cover rounded-full shadow-lg shadow-black/30"
                  />
                </div>

                {/* Info Column - Left aligned */}
                <div className="flex-1 flex flex-col items-start justify-center">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-3xl font-bold text-gray-900 leading-tight">
                      {provider.businessName || provider.providerUsername}
                    </h2>
                    <MdVerified className="text-purple-600 text-2xl" />
                  </div>
                  
                  <div className="flex items-center flex-wrap gap-2 text-sm text-gray-700">
                     {/* Rating */}
                     {rating > 0 ? (
                        <div className="flex items-center gap-1">
                           <span className="font-bold text-gray-900">{rating.toFixed(1)}</span>
                           <div className="flex text-yellow-400 text-xs">
                              {[...Array(5)].map((_, i) => (
                                <FaStar key={i} className={i < Math.round(rating) ? "text-yellow-400" : "text-gray-300"} />
                              ))}
                           </div>
                           <span className="text-gray-500">({reviews?.length || 0})</span>
                        </div>
                     ) : (
                        <span className="text-gray-500">No rating yet</span>
                     )}
                     
                     <span className="text-gray-400">•</span>
                     
                     {/* Location - Address and "Get Directions" link commented out - only showing city and country */}
                     {(provider.city || provider.country) && (
                        <div className="flex items-center gap-1">
                           <span>
                              {/* {provider.address && provider.address}
                              {provider.address && (provider.city || provider.postal) && ", "} */}
                              {provider.city && provider.city}
                              {provider.city && provider.country && ", "}
                              {/* {provider.city && provider.postal && ", "}
                              {provider.postal && provider.postal} */}
                              {provider.country && provider.country}
                           </span>
                           {/* <a 
                             href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${provider.address || ''} ${provider.city || ''} ${provider.postal || ''} ${provider.country || ''}`.trim())}`}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-blue-600 hover:underline font-medium"
                           >
                             Get directions
                           </a> */}
                        </div>
                     )}

                     {/* Phone */}
                     {provider.phoneNumber && (
                        <>
                           <span className="text-gray-400">•</span>
                           <span>{provider.phoneNumber}</span>
                        </>
                     )}
                  </div>

                  {/* Social Media */}
                  {provider.socialMediaHandles?.length > 0 && (
                    <div className="flex items-center space-x-3 mt-3">
                      {provider.socialMediaHandles.map(({ platform, url }) => {
                        // normalize platform names if needed
                        const icon = ICONS[platform];
                        if (!icon || !url) return null; // skip unsupported platforms or empty URLs

                        // Format the URL properly based on platform
                        let formattedUrl = url;

                        // Helper function to clean username (remove @ and any existing domain)
                        const cleanUsername = (username) => {
                          return username
                            .replace(/^@/, "") // Remove leading @
                            .replace(/^https?:\/\/(www\.)?/, "") // Remove http/https and www
                            .replace(
                              /^(instagram\.com\/|tiktok\.com\/@?|x\.com\/|twitter\.com\/)/,
                              ""
                            ) // Remove platform domains
                            .split("/")[0]; // Take only the first part if there are slashes
                        };

                        if (platform === "Instagram") {
                          if (!url.startsWith("http")) {
                            const username = cleanUsername(url);
                            formattedUrl = `https://instagram.com/${username}`;
                          }
                        } else if (platform === "TikTok") {
                          if (!url.startsWith("http")) {
                            const username = cleanUsername(url);
                            formattedUrl = `https://tiktok.com/@${username}`;
                          }
                        } else if (platform === "X") {
                          if (!url.startsWith("http")) {
                            const username = cleanUsername(url);
                            formattedUrl = `https://x.com/${username}`;
                          }
                        }

                        return (
                          <a
                            key={platform}
                            href={formattedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                          >
                            <img
                              src={icon}
                              alt={platform}
                              className="h-5 w-5 mr-1"
                            />
                            <span className="text-xs">{platform}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Policies Section - Above Reviews */}
            <section className="mt-6 bg-white border border-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow text-left">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Policies</h3>
              {Array.isArray(policies) && policies.length > 0 ? (
                policies.map((val, index) => (
                  <div key={index} className="mb-3 text-gray-700">
                    <strong className="text-gray-900">{index + 1}. </strong>
                    {val.description}
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No specific policies provided.</p>
              )}
            </section>

            {/* Services List */}
            {!showBookingFlow && (
              <section className="bg-white border border-gray-200 rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow text-left">
                <h1 className="text-2xl font-bold mb-6 text-gray-900">
                  Services
                </h1>
                <ServicesList
                  servicesByCategory={services}
                  onBookClick={handleBookClick}
                  bookingLoading={bookingLoading}
                  currencySymbol={currencyInfo.symbol}
                  currencyCode={currencyInfo.code}
                />
              </section>
            )}

            {/* Booking Flow */}
            {showBookingFlow && selectedServiceDetail && (
              <section className="p-4 rounded" data-booking-flow>
                <BookingFlow
                  userData={userData}
                  providerId={provider.id}
                  selectedServiceDetail={selectedServiceDetail}
                  selectedAddons={selectedAddons}
                  setSelectedAddons={setSelectedAddons}
                  availability={availability}
                  bookings={bookings || []}
                  blockedTimes={blockedTimes}
                  setShowBookingFlow={setShowBookingFlow}
                  onBookingError={handleBookingError}
                  providerPolicies={{
                    requireCardOnFile: provider.requireCardOnFile,
                    cancellationWindowHours: provider.cancellationWindowHours,
                    noShowFeeType: provider.noShowFeeType,
                    noShowFeeAmount: provider.noShowFeeAmount,
                  }}
                  minimumAdvanceNoticeHours={provider.minimumAdvanceNoticeHours || 0}
                  providerTimezone={provider.timeZone}
                  currencySymbol={currencyInfo.symbol}
                  currencyCode={currencyInfo.code}
                />
              </section>
            )}

            {/* Reviews Section */}
            <div className="mt-6 bg-white border border-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow text-left">
              <h3 className="text-xl font-bold mb-4 text-gray-900">Reviews</h3>

              {/* Review Form - Only show if user is logged in and hasn't reviewed */}
              {userData && !userHasReviewed && (
                <div className="mb-4">
                  <ReviewForm
                    providerId={provider.id}
                    userData={userData}
                    onReviewSubmitted={refreshProviderData}
                  />
                </div>
              )}

              {/* Reviews List - Always visible */}
              {Array.isArray(reviews) && reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review, index) => (
                    <div key={index}>
                      <DisplayReview review={review} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No reviews available.</p>
              )}
            </div>

            {/* Mobile/Tablet: AI Assistant Section */}
            <section className="lg:hidden mt-6">
              <AIAssistantForm providerData={providerData} />
            </section>
          </div>

          {/* Right Column: Sticky AI Assistant */}
          <div className="lg:w-5/12 xl:w-1/3 space-y-6">
            {/* AI Assistant Section - Desktop only */}
            <section className="hidden lg:block lg:sticky lg:top-24 max-h-[calc(100vh-6rem)] overflow-y-auto pb-4 custom-scrollbar">
              <AIAssistantForm providerData={providerData} />
            </section>

            {/* Map (optional) */}
            {/* <section className="mt-5">
              <Maps provider={provider} />
            </section> */}
          </div>
        </div>
      </div>

      {/* Powered by uHyve Footer */}
      <div className="border-t border-gray-200 py-8 mt-8">
        <div className="text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-purple-600 transition-colors text-base"
          >
            Powered by{" "}
            <span className="font-bold text-purple-600 hover:text-purple-700 text-lg">
              uHyve
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
